import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { KeyRound, Plus, Edit2, Trash2, Eye, EyeOff, Copy, Check, X, Link2, Folder, FolderPlus, Download } from 'lucide-react';
import InFinestra from '../../components/InFinestra';
import api from '../../api/client';

const EMPTY_FORM = { titolo: '', username: '', password: '', url: '', note: '', category_id: '' };
const SENZA_CATEGORIA = '__none__';

export default function PasswordManager() {
  const { user } = useAuth();
  const p = user?.permessi?.password_manager;
  const isManager = user?.ruolo === 'ROOT' || !!(p?.create || p?.edit || p?.delete);

  const [entries, setEntries] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('');   // '' = tutte
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [revealed, setRevealed] = useState({});   // id -> password in chiaro
  const [copiedId, setCopiedId] = useState(null);
  const [msg, setMsg]         = useState('');
  const isRoot = user?.ruolo === 'ROOT';
  const [showEsporta, setShowEsporta] = useState(false);
  const [pwEsporta, setPwEsporta] = useState('');
  const [erroreEsporta, setErroreEsporta] = useState('');
  const [esportando, setEsportando] = useState(false);

  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const load = async () => {
    const [en, cat] = await Promise.all([api.get('/password-manager'), api.get('/password-manager/categorie')]);
    setEntries(en.data);
    setCategorie(cat.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setShowNew(false); setEditing(null); };

  const addCategoria = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    try {
      await api.post('/password-manager/categorie', { nome: newCatName.trim() });
      setNewCatName(''); load();
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const deleteCategoria = async (id) => {
    if (!window.confirm('Eliminare la categoria? Le voci non vengono eliminate, restano senza categoria.')) return;
    await api.delete(`/password-manager/categorie/${id}`);
    if (filtroCategoria === String(id)) setFiltroCategoria('');
    load();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/password-manager', form);
      resetForm(); load(); toast('Voce creata');
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const startEdit = (entry) => {
    setEditing(entry.id);
    setForm({
      titolo: entry.titolo,
      username: entry.username || '',
      password: '',
      url: entry.url || '',
      note: entry.note || '',
      category_id: entry.category?.id ? String(entry.category.id) : '',
    });
  };

  const handleUpdate = async (e, id) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.put(`/password-manager/${id}`, payload);
      resetForm(); load(); toast('Salvato');
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa voce?')) return;
    await api.delete(`/password-manager/${id}`);
    setRevealed(r => { const { [id]: _, ...rest } = r; return rest; });
    load();
  };

  const toggleReveal = async (id) => {
    if (revealed[id]) { setRevealed(r => { const { [id]: _, ...rest } = r; return rest; }); return; }
    try {
      const res = await api.post(`/password-manager/${id}/reveal`);
      setRevealed(r => ({ ...r, [id]: res.data.password }));
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const copyPassword = async (id) => {
    let pwd = revealed[id];
    if (!pwd) {
      try {
        const res = await api.post(`/password-manager/${id}/reveal`);
        pwd = res.data.password;
      } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); return; }
    }
    await navigator.clipboard.writeText(pwd);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const fmtData = (d) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const renderForm = (onSubmit, submitLabel) => (
    <div style={s.formCard}>
      <div style={s.formTitle}>{submitLabel === 'CREA' ? 'NUOVA VOCE' : 'MODIFICA VOCE'}</div>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={s.field}><label style={s.label}>NOME *</label>
            <input style={s.input} value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} required placeholder="es. Pannello hosting" /></div>
          <div style={s.field}><label style={s.label}>URL</label>
            <input style={s.input} value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div style={s.field}><label style={s.label}>CATEGORIA</label>
            <select style={s.input} value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
              <option value=''>— nessuna categoria —</option>
              {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select></div>
          <div style={s.field}><label style={s.label}>USERNAME</label>
            <input style={s.input} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>PASSWORD {submitLabel === 'CREA' ? '*' : '(lascia vuoto per non modificarla)'}</label>
            <input style={s.input} type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={submitLabel === 'CREA'} /></div>
        </div>
        <div style={s.field}><label style={s.label}>NOTE</label>
          <textarea style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" style={s.btnSec} onClick={resetForm}>ANNULLA</button>
          <button type="submit" style={s.btnPri}>{submitLabel}</button>
        </div>
      </form>
    </div>
  );

  const chiudiEsporta = () => { setShowEsporta(false); setPwEsporta(''); setErroreEsporta(''); };

  // Download completo (solo ROOT): il server richiede di nuovo la password e registra l'operazione
  const esporta = async (e) => {
    e.preventDefault();
    setEsportando(true); setErroreEsporta('');
    try {
      const r = await api.post('/password-manager/esporta', { password: pwEsporta }, { responseType: 'blob' });
      const nome = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'password-manager.csv';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
      chiudiEsporta();
      toast('File scaricato: conservalo in un posto sicuro');
    } catch (err) {
      let m = 'Download non riuscito';
      try { m = JSON.parse(await err.response.data.text()).error || m; } catch {}
      setErroreEsporta(m);
    } finally { setEsportando(false); }
  };

  const entriesFiltrate = entries.filter(e => {
    if (!filtroCategoria) return true;
    if (filtroCategoria === SENZA_CATEGORIA) return !e.category;
    return e.category?.id === Number(filtroCategoria);
  });

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>PASSWORD MANAGER</h1>
          <p style={s.sub}>{isManager ? 'Credenziali condivise dello staff: crea le voci e organizzale in categorie' : 'Credenziali condivise dello staff'}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isRoot && <button style={s.btnSec} onClick={() => setShowEsporta(true)}><Download size={14} /> SCARICA TUTTO</button>}
          {isManager && <button style={s.btnSec} onClick={() => setShowCatManager(v => !v)}><FolderPlus size={14} /> CATEGORIE</button>}
          {isManager && <button style={s.btnPri} onClick={() => { setShowNew(true); setEditing(null); setForm(EMPTY_FORM); }}><Plus size={14} /> NUOVA VOCE</button>}
        </div>
      </div>

      {msg && <div style={s.toast}>{msg}</div>}

      {showEsporta && (
        <InFinestra attiva onChiudi={chiudiEsporta} larghezza={480}>
          <form onSubmit={esporta} style={{ ...s.formCard, marginBottom: 0 }}>
            <div style={s.formTitle}>SCARICA TUTTE LE PASSWORD</div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '16px' }}>
              Scarichi un file CSV con <b>tutte le password in chiaro</b>, importabile in Bitwarden, 1Password, Chrome o Password di Apple.
              Conservalo in un posto sicuro e cancellalo dopo l'uso. Il download resta registrato nel log.
            </p>
            <div style={s.field}>
              <label style={s.label}>LA TUA PASSWORD DI ACCESSO</label>
              <input style={s.input} type="password" autoFocus autoComplete="current-password" value={pwEsporta} onChange={e => setPwEsporta(e.target.value)} />
            </div>
            {erroreEsporta && <div style={{ color: '#EF4444', fontSize: 'var(--font-size-sm)', marginTop: '10px' }}>{erroreEsporta}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button type="button" style={s.btnSec} onClick={chiudiEsporta}>ANNULLA</button>
              <button type="submit" style={{ ...s.btnPri, opacity: esportando || !pwEsporta ? .6 : 1 }} disabled={esportando || !pwEsporta}><Download size={14} /> {esportando ? 'SCARICO…' : 'SCARICA'}</button>
            </div>
          </form>
        </InFinestra>
      )}

      {showCatManager && isManager && (
        <div style={s.formCard}>
          <div style={s.formTitle}>CATEGORIE</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            {categorie.length === 0 && <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>Nessuna categoria creata.</span>}
            {categorie.map(c => (
              <div key={c.id} style={s.catPill}>
                <Folder size={11} /> {c.nome}
                <button type="button" style={s.removeT} onClick={() => deleteCategoria(c.id)}><X size={10} /></button>
              </div>
            ))}
          </div>
          <form onSubmit={addCategoria} style={{ display: 'flex', gap: '8px' }}>
            <input style={s.input} placeholder="Nome nuova categoria..." value={newCatName} onChange={e => setNewCatName(e.target.value)} />
            <button type="submit" style={s.btnSec}>AGGIUNGI</button>
          </form>
        </div>
      )}

      {showNew && renderForm(handleCreate, 'CREA')}

      {categorie.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          <button style={s.filterPill(filtroCategoria === '')} onClick={() => setFiltroCategoria('')}>TUTTE</button>
          {categorie.map(c => (
            <button key={c.id} style={s.filterPill(filtroCategoria === String(c.id))} onClick={() => setFiltroCategoria(String(c.id))}>
              <Folder size={11} /> {c.nome}
            </button>
          ))}
          <button style={s.filterPill(filtroCategoria === SENZA_CATEGORIA)} onClick={() => setFiltroCategoria(SENZA_CATEGORIA)}>SENZA CATEGORIA</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {entriesFiltrate.length === 0 && (
          <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', padding: '20px' }}>
            {entries.length === 0
              ? (isManager ? 'Nessuna voce creata.' : 'Nessuna credenziale salvata.')
              : 'Nessuna voce in questa categoria.'}
          </div>
        )}
        {entriesFiltrate.map(entry => (
          editing === entry.id ? (
            <div key={entry.id}>{renderForm((e) => handleUpdate(e, entry.id), 'SALVA')}</div>
          ) : (
            <div key={entry.id} style={s.card}>
              <div style={s.cardIcon}><KeyRound size={16} color="#F4600C" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '700', fontSize: 'var(--font-size-base)' }}>{entry.titolo}</span>
                  {entry.category && <span style={s.catBadge}><Folder size={10} /> {entry.category.nome}</span>}
                  {entry.url && (
                    <a href={entry.url} target="_blank" rel="noreferrer" style={s.urlLink}><Link2 size={11} /> {entry.url}</a>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '6px' }}>
                  {entry.username && (
                    <div style={s.credRow}><span style={s.credLabel}>Username</span><span style={{ fontFamily: 'monospace' }}>{entry.username}</span></div>
                  )}
                  <div style={s.credRow}>
                    <span style={s.credLabel}>Password</span>
                    <span style={{ fontFamily: 'monospace', letterSpacing: revealed[entry.id] ? 'normal' : '2px' }}>
                      {revealed[entry.id] || '••••••••••'}
                    </span>
                    <button style={s.iconBtnSmall} onClick={() => toggleReveal(entry.id)} title={revealed[entry.id] ? 'Nascondi' : 'Mostra'}>
                      {revealed[entry.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button style={s.iconBtnSmall} onClick={() => copyPassword(entry.id)} title="Copia">
                      {copiedId === entry.id ? <Check size={13} color="#22C55E" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>

                {entry.note && <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', marginBottom: '6px' }}>{entry.note}</div>}

                <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>
                  {entry.createdBy && <>Creata da {entry.createdBy.nome} il {fmtData(entry.created_at)}</>}
                  {entry.updatedBy && <> · Modificata da {entry.updatedBy.nome} il {fmtData(entry.updated_at)}</>}
                </div>
              </div>
              {isManager && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={s.iconBtn} onClick={() => startEdit(entry)}><Edit2 size={13} /></button>
                  <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={() => handleDelete(entry.id)}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px' },
  btnPri: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color:'#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec: { background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  formCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '20px' },
  formTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: '700', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', fontFamily: 'inherit' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: '14px' },
  cardIcon: { width: '36px', height: '36px', background: '#F4600C15', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' },
  urlLink: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', textDecoration: 'none' },
  catBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border2)', borderRadius: '4px', padding: '2px 8px', fontSize: 'var(--font-size-xs)', fontWeight: '700' },
  catPill: { display: 'flex', alignItems: 'center', gap: '6px', background: '#F4600C18', border: '1px solid #F4600C40', borderRadius: '6px', padding: '4px 10px', fontSize: 'var(--font-size-xs)', fontWeight: '700', color: '#F4600C' },
  removeT: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#F4600C', display: 'flex', padding: 0 },
  filterPill: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
    border: '1px solid ' + (active ? '#F4600C' : 'var(--border2)'),
    background: active ? '#F4600C20' : 'var(--bg-input)', color: active ? '#F4600C' : 'var(--text-muted)',
    borderRadius: '6px', padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: '700',
  }),
  credRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--font-size-sm)' },
  credLabel: { color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' },
  iconBtnSmall: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '5px', padding: '4px 6px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  iconBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 9px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  toast: { background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: 'var(--font-size-sm)' },
};
